import { inject, Injectable } from '@angular/core';
import { Observable, switchMap, take } from 'rxjs';
import { RouteIds, Step } from '../types';
import {
  ApplicantStepResolverService,
  BookingStepResolverService,
  ConfirmationStepResolverService,
  CourseStepResolverService,
  ExtrasStepResolverService,
  PaymentStepResolverService,
  QuoteStepResolverService,
  StepResolverStrategy,
} from './step-resolver';
import { QuoteToolLiteStore } from '../store';
import { AyaQuoteStepResolverService } from './step-resolver/aya-quote-step-resolver.service';

/**
 * Service to resolve the steps.
 * It has a list of strategies (one per each step) to resolve each step
 * based on the step id.
 *
 */
@Injectable({
  providedIn: 'root',
})
export class StepResolverService {
  private readonly courseStepResolverService = inject(CourseStepResolverService);
  private readonly extrasStepResolverService = inject(ExtrasStepResolverService);
  private readonly applicantStepResolverService = inject(ApplicantStepResolverService);
  private readonly quoteStepResolverService = inject(QuoteStepResolverService);
  private readonly ayaQuoteStepResolverService = inject(AyaQuoteStepResolverService);
  private readonly bookingStepResolverService = inject(BookingStepResolverService);
  private readonly confirmationStepResolverService = inject(ConfirmationStepResolverService);
  private readonly paymentStepResolverService = inject(PaymentStepResolverService);
  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);

  private readonly stepResolvers: Record<RouteIds, StepResolverStrategy> = {
    course: this.courseStepResolverService,
    extras: this.extrasStepResolverService,
    applicant: this.applicantStepResolverService,
    quote: this.quoteStepResolverService,
    'aya-quote': this.ayaQuoteStepResolverService,
    booking: this.bookingStepResolverService,
    confirmation: this.confirmationStepResolverService,
    payment: this.paymentStepResolverService,
  };

  /**
   * It resolves the step calling the corresponding strategy by stepId.
   */
  resolve(stepId: RouteIds): Observable<Step> {
    return this.quoteToolLiteStore.activeFlow$.pipe(
      take(1),
      switchMap((activeFlow) => this.stepResolvers[stepId]?.resolve(activeFlow)),
    );
  }
}
